import Decimal from "decimal.js";

export type ParsedPrizePool = {
  prefix: string;
  amount: number;
  suffix: string;
  isCompact: boolean;
};

const COMPACT_MULTIPLIERS: Record<string, number> = {
  K: 1_000,
  M: 1_000_000,
  B: 1_000_000_000,
  T: 1_000_000_000_000,
};

/**
 * Safely parses a free-form prize pool string (e.g. `"$72,000 USDT"`,
 * `"€1.25M USDT"`, `"1,000,000 USDC"`) into its animated-number parts.
 * Uses Decimal.js for numeric extraction (never `parseFloat` on monetary
 * strings). Returns `null` when the amount cannot be cleanly parsed, in
 * which case callers should render the raw string.
 */
export function parsePrizePool(raw: string): ParsedPrizePool | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  // Pull a leading currency symbol (single non-alphanumeric, non-space char).
  const prefixMatch = trimmed.match(/^[^\w\s.,-]/);
  const prefix = prefixMatch?.[0] ?? "";
  const rest = prefix ? trimmed.slice(prefix.length).trim() : trimmed;

  // Capture the numeric token with an optional compact magnitude suffix (K / M / B / T).
  const numericMatch = rest.match(/^([\d,.]+)\s*([KMBTkmbt]?)\s*(.*)$/);
  if (!numericMatch) return null;

  const numericToken = numericMatch[1] ?? "";
  const magnitudeToken = numericMatch[2] ?? "";
  const trailing = numericMatch[3] ?? "";
  if (!numericToken) return null;

  const normalized = numericToken.replace(/,/g, "");
  let amount: Decimal;
  try {
    amount = new Decimal(normalized);
  } catch {
    return null;
  }
  if (!amount.isFinite()) return null;

  const magnitude = magnitudeToken.toUpperCase();
  const multiplier = COMPACT_MULTIPLIERS[magnitude];
  if (multiplier !== undefined) amount = amount.mul(multiplier);

  const amountNumber = amount.toNumber();
  if (!Number.isFinite(amountNumber)) return null;

  return {
    prefix,
    amount: amountNumber,
    suffix: trailing.trim(),
    isCompact: magnitude !== "",
  };
}

/**
 * Convenience wrapper for callers that only need the numeric USD amount
 * from a free-form prize/reward label (e.g. `"$5,000 USDT"` -> `5000`).
 * Returns `null` when the amount cannot be recovered.
 */
export function parsePrizePoolAmount(raw: string | undefined | null): number | null {
  if (!raw) return null;
  return parsePrizePool(raw)?.amount ?? null;
}
